import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import AdminLayout from "../../masterLayout/AdminLayout";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { adminApi, handleAdminLogoutIfNeeded } from "../../utils/adminApi";

const defaultForm = {
  name: "",
  price: 0,
  instance_limit: 1,
  msglimit: 1000,
  contactlimit: 1000,
  templetlimit: 10,
  playDays: 30,
  plantype: "paid",
  allow_webhook: 1,
  allowapi: 1,
  allowchatbot: 0,
  allowbulkmsg: 1,
  allowschedulemsg: 0,
  allow_multi_instance: 0,
};

const FLAG_META = [
  ["allow_webhook", "Webhook"],
  ["allowapi", "API"],
  ["allowchatbot", "Chatbot"],
  ["allowbulkmsg", "Bulk"],
  ["allowschedulemsg", "Schedule"],
  ["allow_multi_instance", "Multi instance"],
];

const planToForm = (plan) => ({
  id: plan.id,
  name: plan.name || "",
  price: Number(plan.price) || 0,
  instance_limit: Number(plan.instance) || 1,
  msglimit: Number(plan.msglimit) || 0,
  contactlimit: Number(plan.contactlimit) || 0,
  templetlimit: Number(plan.templetlimit) || 0,
  playDays: Number(plan.planexpire) || 30,
  plantype: Number(plan.price) <= 0 ? "free" : "paid",
  allow_webhook: Number(plan.allow_webhook) ? 1 : 0,
  allowapi: Number(plan.allowapi) ? 1 : 0,
  allowchatbot: Number(plan.allowchatbot) ? 1 : 0,
  allowbulkmsg: Number(plan.allowbulkmsg) ? 1 : 0,
  allowschedulemsg: Number(plan.allowschedulemsg) ? 1 : 0,
  allow_multi_instance: Number(plan.allow_multi_instance) ? 1 : 0,
});

const AdminPlans = () => {
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);

  const editing = Boolean(form?.id);

  const load = async () => {
    const res = await adminApi({ url: "plan/get-all", method: "GET" });
    if (handleAdminLogoutIfNeeded(res)) return;
    if (res.success) setPlans(res.data || []);
  };

  useEffect(() => {
    document.title = "Admin Plans";
    load().catch(console.error);
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      plantype: Number(form.price) <= 0 ? "free" : form.plantype || "paid",
    };
    const res = await adminApi({
      url: editing ? "plan/update" : "plan/add",
      method: "POST",
      body: editing ? { ...payload, planid: form.id } : payload,
    });
    setSaving(false);
    if (handleAdminLogoutIfNeeded(res)) return;
    setMsg(res.msg || (res.success ? (editing ? "Plan updated" : "Plan added") : "Failed"));
    if (res.success) {
      setForm(null);
      load();
    }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete plan ${row.name}?`)) return;
    const res = await adminApi({
      url: "plan/delete",
      method: "POST",
      body: { planid: row.id },
    });
    if (handleAdminLogoutIfNeeded(res)) return;
    setMsg(res.msg || (res.success ? "Deleted" : "Failed"));
    if (res.success) {
      if (form?.id === row.id) setForm(null);
      load();
    }
  };

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  return (
    <AdminLayout>
      <div className="admin-ui">
        <AdminPageHeader
          title="Plans & pricing"
          subtitle="Create and edit packages users can buy. Limits and feature flags apply instantly."
          action={
            !form ? (
              <button
                type="button"
                className="admin-btn admin-btn-primary"
                onClick={() => setForm({ ...defaultForm })}
              >
                <Icon icon="mdi:plus" width={18} />
                Add plan
              </button>
            ) : null
          }
        />

        {msg ? <div className="admin-alert admin-alert-info">{msg}</div> : null}

        {form && (
          <div className="admin-form-card">
            <div className="admin-form-head">
              <h6>{editing ? `Edit plan #${form.id}` : "New plan"}</h6>
              <p>Set pricing, limits, and which features this package unlocks.</p>
            </div>
            <form onSubmit={save}>
              <div className="admin-form-section">
                <p className="admin-form-section-title">Basics</p>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Starter"
                      value={form.name}
                      onChange={(e) => setField("name", e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Price</label>
                    <input
                      type="number"
                      className="form-control"
                      value={form.price}
                      onChange={(e) => {
                        const price = Number(e.target.value);
                        setForm((f) => ({
                          ...f,
                          price,
                          plantype: price <= 0 ? "free" : "paid",
                        }));
                      }}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Plan type</label>
                    <select
                      className="form-select"
                      value={form.plantype}
                      onChange={(e) => setField("plantype", e.target.value)}
                    >
                      <option value="paid">Paid</option>
                      <option value="free">Free</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Plan days</label>
                    <input
                      type="number"
                      className="form-control"
                      value={form.playDays}
                      onChange={(e) => setField("playDays", Number(e.target.value))}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="admin-form-section">
                <p className="admin-form-section-title">Limits</p>
                <div className="row g-3">
                  {[
                    ["instance_limit", "Instance limit"],
                    ["msglimit", "Message limit"],
                    ["contactlimit", "Contact limit"],
                    ["templetlimit", "Template limit"],
                  ].map(([key, label]) => (
                    <div className="col-md-3" key={key}>
                      <label className="form-label">{label}</label>
                      <input
                        type="number"
                        className="form-control"
                        value={form[key]}
                        onChange={(e) => setField(key, Number(e.target.value))}
                        required
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="admin-form-section">
                <p className="admin-form-section-title">Features</p>
                <div className="admin-toggle-grid">
                  {FLAG_META.map(([key, label]) => (
                    <label
                      key={key}
                      className={`admin-toggle ${form[key] ? "is-on" : ""}`}
                      htmlFor={`plan-${key}`}
                    >
                      <input
                        id={`plan-${key}`}
                        type="checkbox"
                        checked={!!form[key]}
                        onChange={(e) => setField(key, e.target.checked ? 1 : 0)}
                      />
                      <span className="admin-toggle-box" aria-hidden="true" />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="admin-form-actions">
                <button
                  type="submit"
                  className="admin-btn admin-btn-primary"
                  disabled={saving}
                >
                  {saving ? "Saving…" : editing ? "Update plan" : "Save plan"}
                </button>
                <button
                  type="button"
                  className="admin-btn admin-btn-ghost"
                  onClick={() => setForm(null)}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {plans.length === 0 ? (
          <div className="admin-panel-card admin-empty">
            No plans yet. Click <strong>Add plan</strong> to create your first package.
          </div>
        ) : (
          <div className="admin-plan-grid">
            {plans.map((plan, idx) => (
              <article
                key={plan.id}
                className={`admin-plan-card ${idx === 0 ? "featured" : ""} ${
                  form?.id === plan.id ? "is-editing" : ""
                }`}
              >
                <div className="admin-plan-top">
                  <h3 className="admin-plan-name">{plan.name}</h3>
                  <span className="admin-chip">#{plan.id}</span>
                </div>
                <p className="admin-plan-price">
                  {Number(plan.price || 0).toLocaleString()}
                  <small> / {plan.planexpire || "—"} days</small>
                </p>
                <ul className="admin-plan-meta">
                  <li>
                    <Icon icon="mdi:whatsapp" className="ok" width={18} />
                    {plan.instance ?? 0} instances
                  </li>
                  <li>
                    <Icon icon="mdi:message-text-outline" className="ok" width={18} />
                    {(plan.msglimit ?? 0).toLocaleString()} messages
                  </li>
                  <li>
                    <Icon icon="mdi:account-box-outline" className="ok" width={18} />
                    {(plan.contactlimit ?? 0).toLocaleString()} contacts
                  </li>
                  <li>
                    <Icon icon="mdi:file-document-outline" className="ok" width={18} />
                    {plan.templetlimit ?? 0} templates
                  </li>
                </ul>
                <div className="admin-plan-flags">
                  {FLAG_META.map(([key, label]) => (
                    <span key={key} className={`admin-chip ${plan[key] ? "" : "off"}`}>
                      {label}
                    </span>
                  ))}
                </div>
                <div className="admin-plan-actions">
                  <button
                    type="button"
                    className="admin-btn admin-btn-ghost"
                    onClick={() => {
                      setMsg("");
                      setForm(planToForm(plan));
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  >
                    <Icon icon="mdi:pencil-outline" width={16} />
                    Edit
                  </button>
                  <button
                    type="button"
                    className="admin-btn admin-btn-danger"
                    onClick={() => del(plan)}
                  >
                    <Icon icon="lucide:trash-2" width={16} />
                    Delete
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminPlans;
